"use client";

import { useEffect, useMemo, useState } from "react";
import { Clock } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  getCountdownView,
  type CountdownTier,
  type CountdownUnitKey,
} from "@/lib/feed/countdown";

/**
 * <Countdown> — renders the pure `getCountdownView` decision and self-ticks.
 * `variant="card"` is the translucent-ink overlay pill used on item cards (warm
 * amber/ember on urgency, animated clock); `variant="inline"` is plain colored
 * text. Heat is never foil — see .tondocs/ui/FOIL.md and lib/feed/countdown.ts.
 */

const UNIT_LABEL: Record<CountdownUnitKey, string> = {
  mo: "mo",
  d: "d",
  h: "h",
  m: "m",
  s: "s",
  ms: "",
};

const CARD_TIER: Record<CountdownTier, string> = {
  neutral: "bg-black/55 text-white",
  heating: "bg-black/60 text-amber-300",
  final: "bg-[#241503]/85 text-amber-200",
};

const INLINE_TIER: Record<CountdownTier, string> = {
  neutral: "text-muted-foreground",
  heating: "text-[#B67514] dark:text-amber-400",
  final: "text-[#B67514] dark:text-amber-400",
};

export function Countdown({
  endsAt,
  variant = "inline",
  className,
}: {
  endsAt: string | number | Date;
  variant?: "inline" | "card";
  className?: string;
}) {
  const target = useMemo(() => new Date(endsAt).getTime(), [endsAt]);
  // `now` starts null so SSR and first client paint agree (no hydration
  // mismatch); it's set on mount, then each render schedules the next tick.
  const [now, setNow] = useState<number | null>(null);

  useEffect(() => {
    setNow(Date.now());
  }, []);

  useEffect(() => {
    if (now === null) return;
    const { tier, expired } = getCountdownView(target - now);
    if (expired) return;
    // Tick faster in the final minute so the centiseconds move.
    const period = tier === "final" ? 50 : 1000;
    const id = setTimeout(() => setNow(Date.now()), period);
    return () => clearTimeout(id);
  }, [now, target]);

  const view = now === null ? null : getCountdownView(target - now);
  const tier: CountdownTier = view?.tier ?? "neutral";

  const digits =
    view === null || view.expired
      ? null
      : view.units.map((u) => String(u.value).padStart(2, "0")).join(":");

  const label =
    view === null || view.expired
      ? null
      : view.units.map((u) => `${u.value}${UNIT_LABEL[u.key]}`).join(" ");

  if (variant === "card") {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums backdrop-blur-sm",
          CARD_TIER[tier],
          className,
        )}
        aria-label={label ?? undefined}
      >
        <Clock className={cn("size-3.5", tier !== "neutral" && "kr-tick")} aria-hidden />
        <span suppressHydrationWarning>{digits ?? "··:··:··"}</span>
      </span>
    );
  }

  return (
    <span
      className={cn("font-mono text-sm tabular-nums", INLINE_TIER[tier], className)}
      aria-label={label ?? undefined}
      suppressHydrationWarning
    >
      {digits ?? "··:··:··"}
    </span>
  );
}
